import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon, SearchIcon } from 'lucide-react';
import { ProJob } from '../../types/pro';
interface JobPickerProps {
  jobs: ProJob[];
  value: string;
  onChange: (jobId: string) => void;
  disabled?: boolean;
  label: string;
  emptyLabel?: string;
}
function normalize(value: string): string {
  return value.toLowerCase().replace(/æ/g, 'ae').replace(/ø/g, 'oe').replace(/å/g, 'aa');
}

/**
 * Job selector with search. A plain dropdown stops being usable past a
 * couple of dozen jobs, and on site you know the O-number or the customer,
 * not the position in a list.
 */
export function JobPicker({
  jobs,
  value,
  onChange,
  disabled = false,
  label,
  emptyLabel = 'Ingen aktive opgaver'
}: JobPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const selected = jobs.find(job => job.id === value);
  const results = useMemo(() => {
    const term = normalize(query.trim());
    if (!term) return jobs;
    return jobs.filter(job => normalize([job.jobNo, job.title, job.customerName, job.address, job.city].join(' ')).includes(term));
  }, [jobs, query]);
  useEffect(() => {
    if (isOpen) inputRef.current?.focus();else setQuery('');
  }, [isOpen]);
  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);
  return <div ref={containerRef} className="relative block text-left" data-id="element-433" data-mp="o1ucpf4nc2eo">
      <span id="job-picker-label" className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-stone-400" data-id="element-434" data-mp="o003gy2qlyqf">
        {label}
      </span>

      <button type="button" onClick={() => setIsOpen(open => !open)} disabled={disabled || jobs.length === 0} aria-haspopup="listbox" aria-expanded={isOpen} aria-labelledby="job-picker-label" className="flex w-full items-center gap-2 rounded-lg border border-stone-200 px-3 py-2.5 text-left text-sm outline-none transition-colors focus:ring-2 focus:ring-brand-500 disabled:bg-stone-50 disabled:text-stone-400" data-id="element-435" data-mp="2dig7nnzzyd18">
        <span className="min-w-0 flex-1 truncate" data-id="element-436" data-mp="3192ejby9lr9n">
          {selected ? <>
              <span className="font-mono text-xs text-stone-400" data-id="element-437" data-mp="2n9kokt98g7yp">
                {selected.jobNo}
              </span>{' '}
              <span className="text-stone-900" data-id="element-438" data-mp="2s6t40m2zxxw9">{selected.title}</span>
            </> : <span className="text-stone-400" data-id="element-439" data-mp="1bt31vkvftjp9">{emptyLabel}</span>}
        </span>
        <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-stone-400" data-id="element-440" data-mp="1gu0dtfputhib" />
      </button>

      {isOpen && <div className="absolute left-0 right-0 top-full z-30 mt-1.5 overflow-hidden rounded-xl border border-stone-200 bg-white shadow-lg" data-id="element-441" data-mp="18anh6n8773u5">
          <div className="relative border-b border-stone-100" data-id="element-442" data-mp="1ia4h0o0rqni7">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" data-id="element-443" data-mp="1i9mej9nijd1u" />
            <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
          if (e.key === 'Escape') setIsOpen(false);
          if (e.key === 'Enter' && results[0]?.id) {
            onChange(results[0].id);
            setIsOpen(false);
          }
        }} placeholder="Søg nummer, opgave eller kunde…" aria-label="Søg opgave" className="w-full py-2.5 pl-9 pr-3 text-sm outline-none placeholder:text-stone-400" data-id="element-444" data-mp="1d98f2fzz9bff" />
          </div>

          <ul role="listbox" className="max-h-64 overflow-y-auto py-1" data-id="element-445" data-mp="31a5djf2ggg6z">
            {results.length === 0 && <li className="px-3 py-6 text-center text-sm text-stone-500" data-id="element-446" data-mp="368gojt4hkwqs">
                Ingen opgaver matcher “{query}”
              </li>}
            {results.map(job => {
          const isSelected = job.id === value;
          return <li key={job.id} role="option" aria-selected={isSelected} data-id="element-447" data-mp="3f34vdnppmmhp">
                  <button type="button" onClick={() => {
              if (job.id) onChange(job.id);
              setIsOpen(false);
            }} className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${isSelected ? 'bg-brand-50' : 'hover:bg-stone-50'}`} data-id="element-448" data-mp="3k050nweqjl7r">
                    <span className="w-12 flex-shrink-0 font-mono text-xs text-stone-400" data-id="element-449" data-mp="23m532s3d47no">
                      {job.jobNo}
                    </span>
                    <span className="min-w-0 flex-1" data-id="element-450" data-mp="1w3du87ojx9cc">
                      <span className="block truncate text-sm font-medium text-stone-900" data-id="element-451" data-mp="1rc5lzeyyju0r">
                        {job.title}
                      </span>
                      <span className="block truncate text-xs text-stone-500" data-id="element-452" data-mp="1md2hbvmw3ptb">
                        {job.customerName}
                      </span>
                    </span>
                    {isSelected && <CheckIcon className="h-4 w-4 flex-shrink-0 text-brand-600" data-id="element-453" data-mp="2ezdpbrctst05" />}
                  </button>
                </li>;
        })}
          </ul>
        </div>}
    </div>;
}